import { DeleteOutlined, PlayCircleOutlined } from '@ant-design/icons';
import { Button, Card, Divider, Image, Popconfirm, Tooltip } from 'antd';
import axios from 'axios';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { deleteFile } from '../../fetchHooks/deleteFile.ts';
import { envApiUrl } from '../../fetchHooks/envApiUrl.ts';
import type { FileType } from '../../fetchHooks/useAllFiles.ts';
import { FileDownloadLink } from '../../utils/utilFunctions.tsx';
import styles from './FileItem.module.css';
import FileUpdateDebounced from './FileUpdateDebounced.tsx';
import { useEscapeKey } from './Files.tsx';
import { ShowPDF } from './ShowPDF.tsx';
import { VideoPlayerModal } from './VideoPlayerModal.tsx';

export const getFileSize = (sizeInBytes: number) =>
  typeof sizeInBytes === 'number' && sizeInBytes ? (sizeInBytes / 1024 / 1024).toFixed(2) : '';

interface FileItemProps {
  file: FileType;
  refetch: () => void;
  compact?: boolean;
  isEdit?: boolean;
}
const FileItem = ({ file, refetch, compact = false, isEdit = false }: FileItemProps) => {
  const { t } = useTranslation();
  const [showVideoModal, setShowVideoModal] = useState<string | false>(false);
  const [showPdfModal, setShowPdfModal] = useState<string | false>(false);
  const [videoPoster, setVideoPoster] = useState<string | undefined>(undefined);
  const videoElementRef = useRef<HTMLVideoElement | null>(null);

  const { id, mimetype, name, description } = file;
  const regexVideo = /\.(mp4|avi|flv|wmv|mov|mkv|m4v)$/i;
  const regexPdf = /\.(pdf)$/i;

  const isVideo = regexVideo.test(name);
  const isPdf = mimetype === 'application/pdf';
  const size = getFileSize(file.size);
  const pdfIconSrc =
    'https://img.freepik.com/free-vector/illustration-data-folder-icon_53876-6329.jpg?w=1380&t=st=1679521507~exp=1679522107~hmac=6e52b5281e8ed85847fcdabb8a2044f86c04ab137b4d4f200e336d945b75a463';
  let iconImageSrc = pdfIconSrc;
  if (isVideo)
    iconImageSrc = videoPoster || 'https://www.freeiconspng.com/thumbs/video-play-icon/video-play-icon-24.png';
  if (regexPdf.test(name))
    iconImageSrc =
      'https://cdn-icons-png.flaticon.com/512/337/337946.png?w=1380&t=st=1679521403~exp=1679522003~hmac=41a081ab325045db6fc501f53c61606413df69f6c6165da15fcd092ffb786714';

  const handleShowVideoModal = async (id: number) => {
    try {
      const response = await axios.get(`${envApiUrl}/files/video-token/${id}`, {
        withCredentials: true,
      });
      setShowVideoModal(`${envApiUrl}/files/video/${response.data.token}`);
    } catch (error) {
      alert(t('myday.errorLoadingVideo'));
    }
  };

  useEscapeKey(() => setShowVideoModal(false));

  // biome-ignore lint/correctness/useExhaustiveDependencies: Full control of what triggers this code is needed
  useEffect(() => {
    if (!isVideo) return;
    let cancelled = false;
    const generate = async () => {
      const cleanupVideo = () => {
        if (videoElementRef.current?.parentNode) {
          videoElementRef.current.parentNode.removeChild(videoElementRef.current);
          videoElementRef.current = null;
        }
      };

      try {
        const tokenResp = await axios.get(`${envApiUrl}/files/video-token/${id}`, { withCredentials: true });
        const videoUrl = `${envApiUrl}/files/video/${tokenResp.data.token}`;

        const video = document.createElement('video');
        // Try 'anonymous' first, fallback to no crossOrigin if CORS fails
        video.crossOrigin = 'anonymous';
        video.preload = 'metadata';
        video.playsInline = true;
        video.setAttribute('playsinline', 'true');
        video.setAttribute('webkit-playsinline', 'true');
        video.muted = true;
        video.style.display = 'none';
        video.style.position = 'absolute';
        video.style.width = '1px';
        video.style.height = '1px';
        video.style.opacity = '0';
        video.src = videoUrl;

        // iOS sometimes requires video to be in DOM
        document.body.appendChild(video);
        videoElementRef.current = video;

        await new Promise<void>((resolve, reject) => {
          const timeout = setTimeout(() => {
            reject(new Error('Timeout loading video metadata'));
          }, 10000); // 10 second timeout

          const onLoaded = () => {
            clearTimeout(timeout);
            resolve();
          };
          const onError = () => {
            clearTimeout(timeout);
            reject(new Error('Failed to load video metadata'));
          };

          // Try loadedmetadata first (more reliable on iOS)
          video.addEventListener('loadedmetadata', onLoaded, { once: true });
          video.addEventListener('loadeddata', onLoaded, { once: true });
          video.addEventListener('error', onError, { once: true });

          // Force load on iOS
          video.load();
        });

        try {
          // Seek a bit into the video to avoid black frames
          // On iOS, seeking might be restricted, so we'll try but not fail if it doesn't work
          if (video.duration && video.duration > 0) {
            const targetTime = Math.min(5, Math.max(1, video.duration * 0.1));
            await new Promise<void>((resolve) => {
              const timeout = setTimeout(() => {
                // If seeking times out, just resolve anyway (use first frame)
                resolve();
              }, 3000);

              const onSeeked = () => {
                clearTimeout(timeout);
                resolve();
              };

              video.addEventListener('seeked', onSeeked, { once: true });
              video.currentTime = targetTime;
            });
          }
        } catch {
          // If seeking fails (common on iOS), continue with first frame
        }

        // Check if we have valid video dimensions
        if (!video.videoWidth || !video.videoHeight || video.videoWidth === 0 || video.videoHeight === 0) {
          throw new Error('Video dimensions not available');
        }

        const canvas = document.createElement('canvas');
        const width = 480;
        const height = Math.max(1, Math.floor((video.videoHeight / video.videoWidth) * width));
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          throw new Error('Could not get canvas context');
        }

        try {
          ctx.drawImage(video, 0, 0, width, height);
          const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
          if (!cancelled && dataUrl && dataUrl !== 'data:,') {
            setVideoPoster(dataUrl);
          }
        } catch (drawError: any) {
          // Canvas drawing might be restricted due to CORS or other browser policies
          console.warn('Could not draw video to canvas:', drawError);

          // If CORS error, try without crossOrigin
          if (drawError.message?.includes('CORS') || drawError.name === 'SecurityError') {
            console.log('CORS error detected, retrying without crossOrigin attribute');
            // Clean up and retry without crossOrigin
            cleanupVideo();

            // Retry with no crossOrigin
            const retryVideo = document.createElement('video');
            retryVideo.preload = 'metadata';
            retryVideo.playsInline = true;
            retryVideo.setAttribute('playsinline', 'true');
            retryVideo.setAttribute('webkit-playsinline', 'true');
            retryVideo.muted = true;
            retryVideo.style.display = 'none';
            retryVideo.style.position = 'absolute';
            retryVideo.style.width = '1px';
            retryVideo.style.height = '1px';
            retryVideo.style.opacity = '0';
            retryVideo.src = videoUrl;

            document.body.appendChild(retryVideo);
            videoElementRef.current = retryVideo;

            try {
              await new Promise<void>((resolve, reject) => {
                const timeout = setTimeout(() => {
                  reject(new Error('Timeout loading video metadata (retry)'));
                }, 10000);

                const onLoaded = () => {
                  clearTimeout(timeout);
                  resolve();
                };
                const onError = () => {
                  clearTimeout(timeout);
                  reject(new Error('Failed to load video metadata (retry)'));
                };

                retryVideo.addEventListener('loadedmetadata', onLoaded, { once: true });
                retryVideo.addEventListener('loadeddata', onLoaded, { once: true });
                retryVideo.addEventListener('error', onError, { once: true });
                retryVideo.load();
              });

              // Try to draw again
              const retryCanvas = document.createElement('canvas');
              retryCanvas.width = width;
              retryCanvas.height = height;
              const retryCtx = retryCanvas.getContext('2d');
              if (retryCtx) {
                retryCtx.drawImage(retryVideo, 0, 0, width, height);
                const retryDataUrl = retryCanvas.toDataURL('image/jpeg', 0.8);
                if (!cancelled && retryDataUrl && retryDataUrl !== 'data:,') {
                  setVideoPoster(retryDataUrl);
                  cleanupVideo();
                  return; // Success, exit early
                }
              }
            } catch (retryError) {
              console.warn('Retry without crossOrigin also failed:', retryError);
              cleanupVideo();
              throw drawError; // Throw original error
            }
          } else {
            throw drawError;
          }
        } finally {
          if (videoElementRef.current === video) {
            cleanupVideo();
          }
        }
      } catch (e: any) {
        if (!cancelled) {
          console.warn('Error generating video thumbnail:', e);

          // Check if it's a 404 error (file not found)
          if (e?.response?.status === 404 || e?.message?.includes('404')) {
            console.warn('Video file not found on server - file may have been deleted or not uploaded');
            // Silently fail for 404 errors - file doesn't exist
            return;
          }

          // Don't show alert on iOS - silently fail to avoid spam
          if (typeof window !== 'undefined' && !/iPhone|iPad|iPod/.test(navigator.userAgent)) {
            // Only show alert for non-404 errors
            if (e?.response?.status !== 404) {
              alert(t('myday.errorGeneratingVideoThumbnail'));
            }
          }
        }
        // Make sure video is cleaned up even on error
        try {
          cleanupVideo();
        } catch {}
      }
    };
    generate();
    return () => {
      cancelled = true;
      // Clean up video element if still in DOM
      if (videoElementRef.current?.parentNode) {
        videoElementRef.current.parentNode.removeChild(videoElementRef.current);
        videoElementRef.current = null;
      }
    };
  }, [id, isVideo]);

  if (isEdit)
    return (
      <div className={styles.wrapper} key={id}>
        <Card
          className={styles.card}
          cover={
            <div
              className={[styles.cover, compact ? styles.coverCompact : styles.coverRegular].filter(Boolean).join(' ')}
            >
              <Image
                preview={false}
                src={iconImageSrc}
                onClick={isVideo ? () => handleShowVideoModal(id) : undefined}
                className={[styles.image, regexPdf.test(name) ? styles.imagePdf : undefined].filter(Boolean).join(' ')}
              />
            </div>
          }
          actions={[
            <Popconfirm
              key="delete"
              title={t('myday.confirmDelete')}
              onConfirm={() => deleteFile(id, refetch)}
              onCancel={() => console.log('Canceled')}
              okText={t('common.yes')}
              cancelText={t('common.no')}
            >
              <Button
                type="primary"
                danger
                shape="round"
                size="middle"
                icon={<DeleteOutlined />}
                ghost
                className="delete-button"
              >
                {t('common.delete')}
              </Button>
            </Popconfirm>,
            <Button
              key="play"
              onClick={() => handleShowVideoModal(id)}
              size="middle"
              icon={<PlayCircleOutlined />}
              style={{ border: 0 }}
              disabled={!isVideo}
            />,
          ]}
        >
          <FileUpdateDebounced fileId={id} label={t('common.name')} propertyName="name" value={name} />
          <FileUpdateDebounced
            fileId={id}
            label={t('common.description')}
            propertyName="description"
            value={description || ''}
          />
        </Card>
        <Divider />
        {isVideo && showVideoModal && (
          <VideoPlayerModal videoUrl={showVideoModal} onClose={() => setShowVideoModal(false)} />
        )}
      </div>
    );

  return (
    <React.Fragment key={id}>
      <Card
        className={styles.card}
        cover={
          <div
            className={[styles.cover, compact ? styles.coverCompact : styles.coverRegular].filter(Boolean).join(' ')}
          >
            <Image
              preview={false}
              src={iconImageSrc}
              onClick={isVideo ? () => handleShowVideoModal(id) : undefined}
              className={[styles.image, regexPdf.test(name) ? styles.imagePdf : undefined].filter(Boolean).join(' ')}
            />
          </div>
        }
        actions={[
          <Popconfirm
            key="delete"
            title={t('myday.confirmDelete')}
            onConfirm={() => deleteFile(id, refetch)}
            onCancel={() => console.log('Canceled')}
            okText={t('common.yes')}
            cancelText={t('common.no')}
          >
            <Button
              type="primary"
              danger
              shape="round"
              size="middle"
              icon={<DeleteOutlined />}
              ghost
              className="delete-button"
            />
          </Popconfirm>,
          !isPdf ? (
            <Button
              key={`show-video-${id}`}
              onClick={() => handleShowVideoModal(id)}
              size="middle"
              icon={<PlayCircleOutlined />}
              style={{ border: 0 }}
              disabled={!isVideo}
            />
          ) : (
            <FileDownloadLink
              key={`download-${id}`}
              fileId={id}
              fileName={name}
              isPdf={isPdf}
              onClick={() => setShowPdfModal(`${envApiUrl}/files/download/${id}`)}
            />
          ),
          <FileDownloadLink key={`download-${id}`} fileId={id} fileName={name} />,
        ]}
      >
        <Tooltip title={name}>
          <b>{`${description}`}</b>
          <p>{`${mimetype}
                  ${size} MB`}</p>
        </Tooltip>
      </Card>
      {showVideoModal && <VideoPlayerModal videoUrl={showVideoModal} onClose={() => setShowVideoModal(false)} />}
      {showPdfModal && (
        <ShowPDF showModal={!!showPdfModal} fileUrl={showPdfModal} onClose={() => setShowPdfModal(false)} />
      )}
    </React.Fragment>
  );
};

export default FileItem;
